
import React, { useState, useEffect } from 'react';
import { Product } from './types';
import ProductCard from './components/ProductCard';
import AISection from './components/AISection';
import Brochure from './components/Brochure';
import AdminPanel from './components/AdminPanel';
import PrivacyModal from './components/PrivacyModal';
import TermsModal from './components/TermsModal';
import { databaseService } from './services/databaseService';
// Fix: Added missing Loader2 icon to the lucide-react imports
import { UtensilsCrossed, Leaf, Heart, Coffee, Star, ChevronRight, MapPin, Instagram, Mail, Key, CheckCircle2, MessageCircle, Loader2 } from 'lucide-react';

// Componente simple para el icono de WhatsApp
const WhatsAppIcon = ({ size = 16 }: { size?: number }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const App: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showBrochure, setShowBrochure] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  
  const NEW_BRAND_LOGO = 'https://images.unsplash.com/photo-1766980932387-0ab27072bd7c?auto=format&fit=crop&q=80&w=200';

  const [logoUrl, setLogoUrl] = useState(() => {
    return localStorage.getItem('artesano_logo') || NEW_BRAND_LOGO;
  });

  const [email, setEmail] = useState('');
  const [subStatus, setSubStatus] = useState<{ type: 'success' | 'error' | null, message: string }>({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    localStorage.setItem('artesano_logo', logoUrl);
  }, [logoUrl]);

  const sendConfirmationEmail = async (targetEmail: string) => {
    console.log(`[EMAIL SERVICE] Enviando confirmación a: ${targetEmail}`);
    await new Promise(resolve => setTimeout(resolve, 1500));
    return true;
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const result = databaseService.addSubscriber(email);
    if (result.success) {
      try {
        await sendConfirmationEmail(email);
        setSubStatus({ type: 'success', message: '¡Suscrito con éxito!' });
        setEmail('');
      } catch (error) {
        setSubStatus({ type: 'error', message: 'Error en el servicio de correo.' });
      }
    } else {
      setSubStatus({ type: 'error', message: result.message });
    }
    setIsSubmitting(false);
    setTimeout(() => setSubStatus({ type: null, message: '' }), 6000);
  };

  const kidsLine: Product[] = [
    {
      id: 'k1',
      name: 'Pan de Yuca / Pan de Bono',
      description: 'Pequeños bollitos de textura etérea que se deshacen en boca, endulzados naturalmente.',
      differentialValue: 'Almidón Agrio de Yuca',
      benefit: 'Libre de gluten y alta en fibra digestiva.',
      image: 'https://images.unsplash.com/photo-1592811760596-a3102c358b5b?auto=format&fit=crop&q=80&w=600',
      price: 'Bajo Pedido',
      weight: '60g'
    },
    {
      id: 'k2',
      name: 'Pan de Oregano',
      description: 'Pan suave y nutritivo con adicion de oregano.',
      differentialValue: 'Clorofila & Hierro Natural',
      benefit: 'Aporta micronutrientes esenciales para el desarrollo.',
      image: 'https://images.unsplash.com/photo-1730021674000-5f4911f61908?auto=format&fit=crop&q=80&w=600',
      price: 'Bajo Pedido',
      weight: '60g'
    },
    {
      id: 'k3',
      name: 'Plátano Power',
      description: 'Pan suave de plátano maduro horneado y un toque de canela.',
      differentialValue: 'Energía Natural',
      benefit: 'Rico en potasio, ideal para la actividad física.',
      image: 'https://images.unsplash.com/photo-1599743271551-da8b8faacc5b?auto=format&fit=crop&q=80&w=600',
      price: 'Bajo Pedido',
      weight: '60g'
    },
    {
      id: 'k4',
      name: 'Trenza de Ñame',
      description: 'Una joya visual de color natural, suave como la seda.',
      differentialValue: 'Prebióticos Naturales',
      benefit: 'Protector de la flora intestinal infantil.',
      image: 'https://images.unsplash.com/photo-1620416328855-f56e4e98637b?auto=format&fit=crop&q=80&w=600',
      price: 'Bajo Pedido',
      weight: '60g'
    }
  ];

  const traditionLine: Product[] = [
    { id: 't1', name: 'Pan de Queso', description: 'Corteza dorada y crujiente con corazón de queso costeño.', differentialValue: 'Queso Costeño 100%', image: 'https://images.unsplash.com/photo-1758714068469-75887ca0b8c3?auto=format&fit=crop&q=80&w=600', price: '***', weight: '70g' },
    { id: 't2', name: 'Pan de Guayaba', description: 'Clásico atemporal con bocadillo y miga de mantequilla.', differentialValue: 'Dulce Balance', image: 'https://images.unsplash.com/photo-1588195481396-2224956928f9?auto=format&fit=crop&q=80&w=600', price: '***', weight: '70g' },
    { id: 't3', name: 'Pan de Arequipe', description: 'Masa brioche rellena de arequipe artesanal.', differentialValue: 'Miga Premium', image: 'https://images.unsplash.com/photo-1679812000098-ff557c197028?auto=format&fit=crop&q=80&w=600', price: '***', weight: '70g' },
    { id: 't4', name: 'Baguette con Queso y Ajo', description: 'Rusticidad elegante de queso y oregano.', differentialValue: 'Sabor a Hogar', image: 'https://images.unsplash.com/photo-1608100742901-fd3edfd7a5a3?auto=format&fit=crop&q=80&w=1000', price: '***', weight: '300g' },
    { id: 't5', name: 'Mogolla de Café', description: 'Infusionada con granos de origen local.', differentialValue: 'Aroma Energizante', image: 'https://images.unsplash.com/photo-1624772423353-342e62547cd8?auto=format&fit=crop&q=80&w=600', price: 'Bajo Pedido', weight: '60g' },
    { id: 't6', name: 'Pan de Molde Esponjoso', description: 'Fermentación lenta para mayor durabilidad y suavidad.', differentialValue: 'Versatilidad Diaria', image: 'https://images.unsplash.com/photo-1717610305352-c7a33128d6fd?auto=format&fit=crop&q=80&w=1000', price: '***', weight: '500g' },
    { id: 't7', name: 'Brioche Burger', description: 'Mantequilla, huevos de campo y estructura inmejorable.', differentialValue: 'Nivel Gourmet', image: 'https://images.unsplash.com/photo-1606171357732-9d47eb9c4540?auto=format&fit=crop&q=80&w=600', price: 'Bajo Pedido', weight: '90g' },
    { id: 't8', name: 'Brioche Perro', description: 'Alargado, sedoso y diseñado para elevar cualquier preparación.', differentialValue: 'Sedosidad Extrema', image: 'https://images.unsplash.com/photo-1719329466156-8be1e22128a8?auto=format&fit=crop&q=80&w=600', price: 'Bajo Pedido', weight: '70g' },
    { id: 't9', name: 'Pan Mantequilla', description: 'Sencillez absoluta con el máximo sabor lácteo.', differentialValue: 'Favorito Clásico', image: 'https://images.unsplash.com/photo-1670001946746-007bb4bda3b3?auto=format&fit=crop&q=80&w=600', price: '***', weight: '60g' },
    { id: 't10', name: 'Croissant', description: 'Capas crujientes, centroesponjoso.', differentialValue: 'Artesanía en Capas', image: 'https://images.unsplash.com/photo-1619540158662-bb74607515f2?auto=format&fit=crop&q=80&w=600', price: '***', weight: '70g' },
  ];

  return (
    <div className={`min-h-screen bg-[#fcfbf7] ${showBrochure || showAdmin || showPrivacy || showTerms ? 'overflow-hidden' : ''}`}>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-2' : 'bg-transparent py-4'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-3 group cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <div className="h-14 w-14 bg-white rounded-xl shadow-lg border border-stone-100 flex items-center justify-center p-0.5 group-hover:scale-105 transition-all overflow-hidden">
              <img src={logoUrl} alt="Arte-Sano Logo" className="w-full h-full object-cover rounded-lg" loading="eager" fetchPriority="high" />
            </div>
            <h1 className="text-xl font-bold tracking-tighter text-stone-900 uppercase hidden sm:block">ARTE-SANO</h1>
          </div>
          <nav className="hidden md:flex gap-10 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
            <a href="#kids" className="hover:text-amber-600 transition-colors">Pequeños Gigantes</a>
            <a href="#tradition" className="hover:text-amber-600 transition-colors">Tradicional</a>
            <a href="#about" className="hover:text-amber-600 transition-colors">Contacto</a>
          </nav>
          <div className="flex items-center gap-4">
             <button 
               onClick={() => setShowBrochure(true)}
               className="bg-stone-900 text-white px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-stone-700 transition-all shadow-lg active:scale-95"
             >
               Catálogo PDF
             </button>
          </div>
        </div>
      </header>

      {showBrochure && (
        <Brochure logoUrl={logoUrl} kidsLine={kidsLine} traditionLine={traditionLine} onClose={() => setShowBrochure(false)} />
      )}
      {showAdmin && (
        <AdminPanel currentLogo={logoUrl} onLogoChange={setLogoUrl} onClose={() => setShowAdmin(false)} />
      )}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}

      <main className="pt-32">
        <section className="max-w-7xl mx-auto px-6 mb-32 grid md:grid-cols-2 gap-16 items-center">
          <div className="animate-in fade-in slide-in-from-left-8 duration-1000">
            <span className="inline-block text-amber-600 text-[10px] font-bold tracking-[0.4em] uppercase mb-6 border-b border-amber-200 pb-1">
              Est. 2022 • Caribe Colombiano
            </span>
            <h2 className="text-6xl md:text-7xl font-bold text-stone-900 mb-8 leading-[1.1] tracking-tight">
              Pan con <br/><span className="italic text-amber-600">conciencia</span> y alma.
            </h2>
            <p className="text-lg text-stone-600 leading-relaxed font-light max-w-lg mb-10">
              Transformamos ingredientes locales en experiencias nutritivas. Un puente entre la tradición de nuestra tierra y el futuro saludable de nuestros hijos.
            </p>
            <div className="flex flex-wrap gap-8 text-stone-400">
              <div className="flex items-center gap-3">
                <Leaf size={18} className="text-emerald-500" />
                <span className="text-[11px] uppercase font-bold tracking-widest">hecho a Mano</span>
              </div>
              <div className="flex items-center gap-3">
                <Heart size={18} className="text-rose-500" />
                <span className="text-[11px] uppercase font-bold tracking-widest">Nutrición Real</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-stone-500" />
                <span className="text-[11px] uppercase font-bold tracking-widest">Origen Local</span>
              </div>
            </div>
          </div>
          <div className="relative animate-in fade-in zoom-in-95 duration-1000">
             <div className="aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl">
               <img src="https://images.unsplash.com/photo-1629677594742-58acc2c5ca82?auto=format&fit=crop&q=80&w=1200" alt="Masa madre artesanal" className="w-full h-full object-cover" fetchPriority="high" />
             </div>
             <div className="absolute -bottom-8 -left-8 bg-white p-8 rounded-2xl shadow-xl border border-stone-100 hidden lg:block max-w-xs">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="text-amber-400 fill-amber-400" size={16} />
                  <span className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">Chef's Choice</span>
                </div>
                <h4 className="text-xl font-bold text-stone-900 mb-2 italic">Galleta Estilo New York</h4>
                <p className="text-xs text-stone-500 leading-relaxed">Galleta de harina elite y mezcla de chocolate blanco y negro 70% cacao.</p>
             </div>
          </div>
        </section>

        <section id="kids" className="max-w-7xl mx-auto px-6 mb-32 scroll-mt-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="text-4xl font-bold text-stone-900 tracking-tight">Línea "Pequeños Gigantes"</h2>
              <p className="text-stone-500 mt-3 font-light text-lg">Nutrición inteligente disfrazada de sabor inolvidable.</p>
            </div>
            <div className="flex gap-2">
               <span className="bg-emerald-50 text-emerald-700 px-4 py-1.5 rounded-full text-[10px] font-bold border border-emerald-100">Sin Azúcar Añadida</span>
               <span className="bg-stone-100 text-stone-600 px-4 py-1.5 rounded-full text-[10px] font-bold">100% Natural</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {kidsLine.map((product) => (
              <ProductCard key={product.id} product={product} badge="Infantil" />
            ))}
          </div>
          <AISection />
        </section>

        <section className="bg-stone-900 text-white py-24 mb-32 overflow-hidden">
           <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
             <div className="order-2 lg:order-1">
               <div className="grid grid-cols-2 gap-4">
                 <div className="space-y-4">
                   <div className="h-48 rounded-2xl overflow-hidden"><img className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1635341814289-55cf1490024c?auto=format&fit=crop&q=80&w=400" alt="Tradición" loading="lazy" /></div>
                   <div className="h-64 rounded-2xl overflow-hidden"><img className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1630329777937-e0de732a893c?auto=format&fit=crop&q=80&w=400" alt="Tradición" loading="lazy" /></div>
                 </div>
                 <div className="space-y-4 pt-12">
                   <div className="h-64 rounded-2xl overflow-hidden"><img className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1656180384586-0f1cde47a9d6?auto=format&fit=crop&q=80&w=400" alt="Tradición" loading="lazy" /></div>
                   <div className="h-48 rounded-2xl overflow-hidden"><img className="w-full h-full object-cover" src="https://images.unsplash.com/photo-1652283305325-009cf368c0e1?auto=format&fit=crop&q=80&w=400" alt="Tradición" loading="lazy" /></div>
                 </div>
               </div>
             </div>
             <div className="order-1 lg:order-2">
               <span className="text-amber-400 text-[10px] font-bold tracking-[0.3em] uppercase mb-4 block">El Arte de la Masa</span>
               <h2 className="text-5xl font-bold mb-8 leading-tight">Tradición que se siente en cada fibra.</h2>
               <p className="text-stone-400 text-lg font-light leading-relaxed mb-10">
                 No es solo pan; es el respeto por el tiempo. Usamos fermentaciones de 8 a 12 horas que resultan en migas ligeras y aromáticas.
               </p>
               <div className="space-y-6">
                 <div className="flex gap-4 items-start">
                   <div className="bg-stone-800 p-3 rounded-xl"><UtensilsCrossed className="text-amber-400" size={20} /></div>
                   <div>
                     <h4 className="font-bold text-lg">Insumos de Alta Calidad</h4>
                     <p className="text-stone-500 text-sm">Harinas seleccionadas y quesos de origen.</p>
                   </div>
                 </div>
                 <div className="flex gap-4 items-start">
                   <div className="bg-stone-800 p-3 rounded-xl"><Coffee className="text-amber-400" size={20} /></div>
                   <div>
                     <h4 className="font-bold text-lg">Horneado Controlado</h4>
                     <p className="text-stone-500 text-sm">Camas de piedra para una distribución perfecta del calor.</p>
                   </div>
                 </div>
               </div>
             </div>
           </div>
        </section>

        <section id="tradition" className="max-w-7xl mx-auto px-6 mb-32 scroll-mt-24">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-stone-900 mb-4 tracking-tight uppercase">Colección "Tradición"</h2>
            <div className="w-16 h-1 bg-amber-400 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {traditionLine.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>

        <section id="about" className="max-w-7xl mx-auto px-6 mb-32">
          <div className="bg-stone-900 text-white rounded-[3rem] p-12 md:p-24 relative overflow-hidden">
             <div className="relative z-10 grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <h2 className="text-5xl font-bold mb-8 uppercase">Pan real para todos.</h2>
                  <p className="text-stone-400 text-xl font-light mb-12">Suscríbete para novedades y preventas exclusivas.</p>
                  <form onSubmit={handleSubscribe} className="relative">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Tu correo electrónico" className="bg-white/10 border border-white/20 px-8 py-4 rounded-full flex-grow text-white focus:outline-none focus:ring-2 focus:ring-amber-400" disabled={isSubmitting} />
                      <button type="submit" disabled={isSubmitting} className="bg-amber-400 text-stone-900 px-10 py-4 rounded-full font-bold text-sm hover:bg-amber-300 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-2">
                        {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : 'Suscribirse'}
                      </button>
                    </div>
                    {subStatus.type && (
                      <div className={`absolute -bottom-10 left-0 flex items-center gap-2 animate-in slide-in-from-top-2 ${subStatus.type === 'success' ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {subStatus.type === 'success' ? <CheckCircle2 size={16} /> : <Star size={16} />}
                        <span className="text-xs font-bold uppercase tracking-widest">{subStatus.message}</span>
                      </div>
                    )}
                  </form>
                </div>
                <div className="hidden lg:block text-center">
                   <div className="grid grid-cols-3 gap-8">
                     <div><p className="text-4xl font-bold mb-2">100+</p><p className="text-[10px] text-stone-500 uppercase font-bold">Ventas</p></div>
                     <div><p className="text-4xl font-bold mb-2">24h</p><p className="text-[10px] text-stone-500 uppercase font-bold">Proceso</p></div>
                     <div><p className="text-4xl font-bold mb-2">0%</p><p className="text-[10px] text-stone-500 uppercase font-bold">Aditivos</p></div>
                   </div>
                </div>
             </div>
          </div>
        </section>
      </main>

      <footer className="bg-stone-50 py-20 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="h-14 w-14 bg-white rounded-xl shadow-sm border border-stone-100 flex items-center justify-center p-0.5 overflow-hidden">
                <img src={logoUrl} alt="Arte-Sano Logo" className="w-full h-full object-cover rounded-lg" loading="lazy" />
              </div>
              <span className="font-bold text-stone-900 tracking-tighter text-xl uppercase">ARTE-SANO</span>
            </div>
          </div>
          <div>
            <h5 className="font-bold text-stone-900 uppercase text-[10px] tracking-[0.2em] mb-6">Líneas</h5>
            <ul className="space-y-4 text-stone-500 text-sm font-light">
              <li><a href="#kids">Infantil</a></li>
              <li><a href="#tradition">Tradición</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-bold text-stone-900 uppercase text-[10px] tracking-[0.2em] mb-6">Contacto</h5>
            <ul className="space-y-4 text-stone-500 text-sm font-light">
              <li className="flex items-center gap-3"><MapPin size={16} /> Cartagena, Colombia</li>
              <li className="flex items-center gap-3"><WhatsAppIcon size={16} /> +57 312 395 9838</li>
            </ul>
          </div>
          <div>
            <h5 className="font-bold text-stone-900 uppercase text-[10px] tracking-[0.2em] mb-6">Horarios</h5>
            <p className="text-stone-500 text-sm leading-relaxed font-light">Lun - Vie: 7:00 AM - 4:00 PM</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 pt-10 border-t border-stone-200 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-stone-400 text-[10px] uppercase font-bold tracking-widest">© 2022 Arte-Sano Panadería • Cartagena</p>
          <div className="flex gap-8 text-[10px] font-bold uppercase tracking-widest text-stone-400">
            <button onClick={() => setShowAdmin(true)} className="hover:text-amber-600 flex items-center gap-1 transition-colors"><Key size={10} /> Admin</button>
            <button onClick={() => setShowPrivacy(true)}>Privacidad</button>
            <button onClick={() => setShowTerms(true)}>Términos</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
